import React, { useState, useEffect, memo } from 'react';
import axios from 'axios';
import AdminSidebarRaw from '../Components/AdminSidebar';
import { API_BASE_URL } from '../config';
import '../Styles/Definitions.css';

const AdminSidebar = memo(AdminSidebarRaw);

const Definitions = () => {
    const [seasons, setSeasons] = useState([]);
    const [attributes, setAttributes] = useState([]);
    const [attributeFamilies, setAttributeFamilies] = useState([]);
    const [attributeValues, setAttributeValues] = useState([]);

    const [selectedAttribute, setSelectedAttribute] = useState(null);
    const [deliverySettings, setDeliverySettings] = useState([]);
    const [orderDiscount, setOrderDiscount] = useState(null);
    const [coupons, setCoupons] = useState([]);

    // Search States
    const [searchSeason, setSearchSeason] = useState('');
    const [searchAttributeFamily, setSearchAttributeFamily] = useState('');
    const [searchAttributeValue, setSearchAttributeValue] = useState('');
    const [searchCoupon, setSearchCoupon] = useState('');
    const [searchDeliverySettings, setSearchDeliverySettings] = useState('');

    // Quick Add States
    const [newSeasonName, setNewSeasonName] = useState('');
    const [newSeasonYear, setNewSeasonYear] = useState(new Date().getFullYear());
    const [newAttributeFamilyName, setNewAttributeFamilyName] = useState('');
    const [selectedAttrForFamily, setSelectedAttrForFamily] = useState('');
    const [newAttributeValueName, setNewAttributeValueName] = useState('');
    const [selectedFamilyForValue, setSelectedFamilyForValue] = useState('');
    const [newCoupon, setNewCoupon] = useState({
        code: '',
        discount_type: 'percentage',
        discount_value: '',
        start_date: new Date().toISOString().slice(0, 10),
        end_date: '',
        is_active: true,
        for_new_signup: false
    });

    const [editOrderDiscount, setEditOrderDiscount] = useState({
        type: 'percentage',
        value: '',
        start_date: '',
        end_date: '',
        is_active: true,
        min_price: ''
    });

    const [isEditingOrderDiscount, setIsEditingOrderDiscount] = useState(false);
    const [hasMinPriceLimit, setHasMinPriceLimit] = useState(false);

    // Delivery Edit States
    const [isEditingDelivery, setIsEditingDelivery] = useState(false);
    const [isEditingDefaultDelivery, setIsEditingDefaultDelivery] = useState(false);
    const [editDeliveryId, setEditDeliveryId] = useState(null);
    const [editDeliveryCity, setEditDeliveryCity] = useState('');
    const [editDeliveryFee, setEditDeliveryFee] = useState('');
    const [editFreeMin, setEditFreeMin] = useState('');
    const [newDeliveryCity, setNewDeliveryCity] = useState('');
    const [newDeliveryFee, setNewDeliveryFee] = useState('');
    const [newDeliveryFreeMin, setNewDeliveryFreeMin] = useState('');

    // Editing States
    const [editingItem, setEditingItem] = useState({ type: null, id: null });
    const [editValue, setEditValue] = useState('');
    const [editYear, setEditYear] = useState('');
    const [editCouponData, setEditCouponData] = useState(null);

    // Dialog States
    const [deleteConfirm, setDeleteConfirm] = useState({ show: false, type: null, id: null });
    const [toggleConfirm, setToggleConfirm] = useState({ show: false, item: null, type: null });
    const [listModal, setListModal] = useState({ show: false, type: 'seasons', title: '' });
    const [modalFilterAttributeId, setModalFilterAttributeId] = useState(null);
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 640);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth <= 640);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        fetchSeasons();
        fetchAttributes();
        fetchAttributeFamilies();
        fetchAttributeValues();
        fetchDeliverySettings();
        fetchCoupons();
        fetchOrderDiscount();
    }, []);

    const fetchSeasons = async () => {
        const adminToken = localStorage.getItem('adminToken');
        try {
            const response = await axios.get(`${API_BASE_URL}/admin/seasons`, {
                headers: { Authorization: `Bearer ${adminToken}` }
            });
            setSeasons(response.data);
        } catch (error) {
            console.error('Error fetching seasons:', error);
        }
    };

    const fetchAttributes = async () => {
        const adminToken = localStorage.getItem('adminToken');
        try {
            const response = await axios.get(`${API_BASE_URL}/admin/attributes`, {
                headers: { Authorization: `Bearer ${adminToken}` }
            });
            setAttributes(response.data);
        } catch (error) {
            console.error('Error fetching attributes:', error);
        }
    };

    const fetchAttributeFamilies = async () => {
        const adminToken = localStorage.getItem('adminToken');
        try {
            const response = await axios.get(`${API_BASE_URL}/admin/attribute-families`, {
                headers: { Authorization: `Bearer ${adminToken}` }
            });
            setAttributeFamilies(response.data);
        } catch (error) {
            console.error('Error fetching attribute families:', error);
        }
    };
    const fetchAttributeValues = async () => {
        const adminToken = localStorage.getItem('adminToken');
        try {
            const response = await axios.get(`${API_BASE_URL}/admin/attribute-values`, {
                headers: { Authorization: `Bearer ${adminToken}` }
            });
            setAttributeValues(response.data);
        } catch (error) {
            console.error('Error fetching attribute values:', error);
        }
    };

    const fetchDeliverySettings = async () => {
        const adminToken = localStorage.getItem('adminToken');
        try {
            const response = await axios.get(`${API_BASE_URL}/admin/delivery-settings`, {
                headers: { Authorization: `Bearer ${adminToken}` }
            });
            if (Array.isArray(response.data)) {
                setDeliverySettings(response.data);
            } else {
                setDeliverySettings([]);
            }
        } catch (error) {
            console.error('Error fetching delivery settings:', error);
        }
    };

    const fetchCoupons = async () => {
        const adminToken = localStorage.getItem('adminToken');
        try {
            const response = await axios.get(`${API_BASE_URL}/admin/coupons`, {
                headers: { Authorization: `Bearer ${adminToken}` }
            });
            setCoupons(response.data);
        } catch (error) {
            console.error('Error fetching coupons:', error);
        }
    };

    const fetchOrderDiscount = async () => {
        const adminToken = localStorage.getItem('adminToken');
        try {
            const response = await axios.get(`${API_BASE_URL}/admin/order-discounts`, {
                headers: { Authorization: `Bearer ${adminToken}` }
            });
            if (response.data && response.data.length > 0) {
                setOrderDiscount(response.data[0]);
            }
        } catch (error) {
            console.error('Error fetching order discount:', error);
        }
    };

    const defaultDeliverySetting = deliverySettings.find(item => (item.city || '').trim().toLowerCase() === 'default') || deliverySettings.find(item => !item.city || item.city.trim() === '') || null;

    const handleAdd = async (type, name, parentId = null) => {
        if (!name.trim()) return;
        const adminToken = localStorage.getItem('adminToken');
        const headers = { Authorization: `Bearer ${adminToken}` };

        try {
            if (type === 'seasons') {
                await axios.post(`${API_BASE_URL}/admin/seasons`, { name, year: newSeasonYear }, { headers });
                setNewSeasonName('');
                setNewSeasonYear(new Date().getFullYear());
                fetchSeasons();
            } else if (type === 'attribute-families') {
                if (!parentId) return alert('Please select an Attribute first');
                await axios.post(`${API_BASE_URL}/admin/attribute-families`, { attribute_id: parentId, name }, { headers });
                setNewAttributeFamilyName('');
                fetchAttributeFamilies();
            } else if (type === 'attribute-values') {
                if (!parentId) {
                    alert('Please select an Attribute first');
                    return;
                }
                await axios.post(`${API_BASE_URL}/admin/attribute-values`, {
                    attribute_id: parentId,
                    family_id: selectedFamilyForValue || null,
                    value: name
                }, { headers });
                setNewAttributeValueName('');
                setSelectedFamilyForValue('');
                fetchAttributeValues();
            } else if (type === 'coupons') {
                const { code, discount_type, discount_value, start_date, end_date, is_active, for_new_signup } = newCoupon;
                if (!code || !discount_value) return alert('Code and value are required');
                await axios.post(`${API_BASE_URL}/admin/coupons`, {
                    code,
                    discount_type,
                    discount_value,
                    start_date: start_date || null,
                    end_date: end_date || null,
                    is_active: is_active ? 1 : 0,
                    for_new_signup: for_new_signup ? 1 : 0
                }, { headers });
                setNewCoupon({
                    code: '',
                    discount_type: 'percentage',
                    discount_value: '',
                    start_date: new Date().toISOString().slice(0, 10),
                    end_date: '',
                    is_active: true,
                    for_new_signup: false
                });
                fetchCoupons();
            }
        } catch (error) {
            console.error('Error adding:', error);
            alert(error.response?.data?.message || 'Failed to add item');
        }
    };

    const handleUpdate = async (type, id, newName) => {
        if (editingItem.id !== id || editingItem.type !== type) return;
        if (!newName.trim()) return setEditingItem({ type: null, id: null });
        const adminToken = localStorage.getItem('adminToken');
        const headers = { Authorization: `Bearer ${adminToken}` };

        try {
            if (type === 'seasons') {
                await axios.put(`${API_BASE_URL}/admin/seasons/${id}`, { name: newName, year: editYear }, { headers });
                fetchSeasons();
            } else if (type === 'attribute-families') {
                const item = attributeFamilies.find(af => af.id === id);
                await axios.put(`${API_BASE_URL}/admin/attribute-families/${id}`, { attribute_id: item.attribute_id, name: newName }, { headers });
                fetchAttributeFamilies();
            } else if (type === 'attribute-values') {
                const item = attributeValues.find(av => av.id === id);
                await axios.put(`${API_BASE_URL}/admin/attribute-values/${id}`, {
                    attribute_id: item.attribute_id,
                    family_id: item.family_id,
                    value: newName
                }, { headers });
                fetchAttributeValues();
            } else if (type === 'coupons') {
                const item = coupons.find(c => c.id === id);
                await axios.put(`${API_BASE_URL}/admin/coupons/${id}`, {
                    ...item,
                    code: newName,
                    is_active: editCouponData.is_active ? 1 : 0,
                    start_date: editCouponData.start_date || null,
                    end_date: editCouponData.end_date || null,
                    for_new_signup: editCouponData.for_new_signup ? 1 : 0
                }, { headers });
                fetchCoupons();
                setEditCouponData(null);
            }

            setEditingItem({ type: null, id: null });
        } catch (error) {
            console.error('Error updating:', error);
            alert('Failed to update');
        }
    };

    const handleDelete = async () => {
        const { type, id } = deleteConfirm;
        const adminToken = localStorage.getItem('adminToken');
        try {
            await axios.delete(`${API_BASE_URL}/admin/${type}/${id}`, {
                headers: { Authorization: `Bearer ${adminToken}` }
            });
            if (type === 'seasons') fetchSeasons();
            if (type === 'attribute-families') fetchAttributeFamilies();
            if (type === 'attribute-values') fetchAttributeValues();
            if (type === 'coupons') fetchCoupons();
            if (type === 'delivery-settings') fetchDeliverySettings();

            setDeleteConfirm({ show: false, type: null, id: null });
        } catch (error) {
            console.error('Error deleting:', error);
            setDeleteConfirm({ show: false, type: null, id: null });
        }
    };

    const openListModal = (type, title) => {
        setModalFilterAttributeId(null);
        if (type === 'delivery-settings') setSearchDeliverySettings('');
        setListModal({ show: true, type, title });
    };
    const closeListModal = () => {
        setListModal({ show: false, type: null, title: '' });
        setModalFilterAttributeId(null);
        setSearchDeliverySettings('');
    };
    const confirmDelete = (type, id) => setDeleteConfirm({ show: true, type, id });

    const startEditing = (type, item) => {
        if (type === 'delivery-settings') {
            closeListModal();
            startEditingDelivery(item);
            return;
        }

        setEditingItem({ type, id: item.id });
        if (type === 'attribute-values') setEditValue(item.value);
        else if (type === 'attribute-families') setEditValue(item.name);
        else if (type === 'coupons') {
            setEditValue(item.code);
            setEditCouponData({
                is_active: !!item.is_active,
                start_date: item.start_date ? new Date(item.start_date).toISOString().slice(0, 10) : '',
                end_date: item.end_date ? new Date(item.end_date).toISOString().slice(0, 10) : '',
                for_new_signup: !!item.for_new_signup
            });
        }
        else {
            setEditValue(item.name);
            if (type === 'seasons') setEditYear(item.year);
        }
    };


    const toggleCouponActive = (item) => {
        setToggleConfirm({ show: true, item, type: 'coupons' });
    };

    const toggleOrderDiscountActive = (item) => {
        setToggleConfirm({ show: true, item, type: 'order-discounts' });
    };

    const handleToggleSeasonSelected = async (season) => {
        const newStatus = season.selected ? 0 : 1;
        const adminToken = localStorage.getItem('adminToken');

        try {
            await axios.put(`${API_BASE_URL}/admin/seasons/${season.id}`, {
                ...season,
                selected: newStatus
            }, {
                headers: { Authorization: `Bearer ${adminToken}` }
            });
            fetchSeasons();
        } catch (error) {
            console.error('Error toggling season selection:', error);
            alert('Failed to update season selection');
        }
    };

    const handleToggleCouponFeatured = async (coupon) => {
        const newStatus = coupon.show_on_home ? 0 : 1;
        const adminToken = localStorage.getItem('adminToken');
        try {
            await axios.put(`${API_BASE_URL}/admin/coupons/${coupon.id}`, {
                ...coupon,
                show_on_home: newStatus
            }, {
                headers: { Authorization: `Bearer ${adminToken}` }
            });
            fetchCoupons();
        } catch (error) {
            console.error('Error toggling coupon featured status:', error);
            alert('Failed to update featured coupon');
        }
    };

    const handleConfirmToggle = async () => {
        if (!toggleConfirm.item || !toggleConfirm.type) return;
        const adminToken = localStorage.getItem('adminToken');
        try {
            const endpoint = toggleConfirm.type === 'coupons' ? 'coupons' : 'order-discounts';
            await axios.put(`${API_BASE_URL}/admin/${endpoint}/${toggleConfirm.item.id}`, {
                ...toggleConfirm.item,
                is_active: toggleConfirm.item.is_active ? 0 : 1
            }, {
                headers: { Authorization: `Bearer ${adminToken}` }
            });

            if (toggleConfirm.type === 'coupons') fetchCoupons();
            else {
                fetchOrderDiscount();
                // If we are currently editing, update the local edit state too
                setEditOrderDiscount(prev => ({
                    ...prev,
                    is_active: !toggleConfirm.item.is_active
                }));
            }

            setToggleConfirm({ show: false, item: null, type: null });
        } catch (error) {
            console.error('Error toggling status:', error);
            alert('Failed to toggle status');
        }
    };

    const startEditingDelivery = (item = null) => {
        const target = item || defaultDeliverySetting;
        setEditDeliveryId(target?.id || null);

        const isDefault = !item ? true : ((item.city || '').trim().toLowerCase() === 'default' || !item.city || item.city.trim() === '');
        setIsEditingDefaultDelivery(isDefault);

        setEditDeliveryCity(isDefault ? 'Default' : (target?.city?.trim() || ''));
        setEditDeliveryFee(target?.delivery_fee || '0');
        setEditFreeMin(target?.free_delivery_min || '0');
        setIsEditingDelivery(true);
    };

    const handleSaveDelivery = async () => {
        const adminToken = localStorage.getItem('adminToken');
        try {
            const deliveryFeeValue = parseFloat(editDeliveryFee);
            const freeMinValue = parseFloat(editFreeMin) || 0;
            const cityValue = editDeliveryCity?.trim();

            if (Number.isNaN(deliveryFeeValue)) {
                return alert('Delivery fee is required');
            }

            const data = {
                delivery_fee: deliveryFeeValue,
                free_delivery_min: freeMinValue
            };
            const isDefaultEdit = isEditingDefaultDelivery;

            if (isDefaultEdit) {
                data.city = 'Default';
            } else {
                if (!cityValue) {
                    return alert('City is required for city-specific delivery settings');
                }
                data.city = cityValue;
            }

            if (editDeliveryId) {
                await axios.put(`${API_BASE_URL}/admin/delivery-settings/${editDeliveryId}`, data, {
                    headers: { Authorization: `Bearer ${adminToken}` }
                });
            } else {
                await axios.post(`${API_BASE_URL}/admin/delivery-settings`, data, {
                    headers: { Authorization: `Bearer ${adminToken}` }
                });
            }

            setIsEditingDelivery(false);
            setEditDeliveryId(null);
            setEditDeliveryCity('');
            setEditDeliveryFee('');
            setEditFreeMin('');
            fetchDeliverySettings();
        } catch (error) {
            console.error('Error saving delivery settings:', error);
            alert(error.response?.data?.message || 'Failed to save delivery settings');
        }
    };

    const handleAddDeliverySetting = async () => {
        const adminToken = localStorage.getItem('adminToken');
        try {
            const cityValue = newDeliveryCity?.trim();
            const deliveryFeeValue = parseFloat(newDeliveryFee);
            const freeMinValue = parseFloat(newDeliveryFreeMin) || 0;

            if (!cityValue) {
                return alert('City is required to add a new delivery fee');
            }
            if (Number.isNaN(deliveryFeeValue)) {
                return alert('Delivery fee is required');
            }

            await axios.post(`${API_BASE_URL}/admin/delivery-settings`, {
                city: cityValue,
                delivery_fee: deliveryFeeValue,
                free_delivery_min: freeMinValue
            }, {
                headers: { Authorization: `Bearer ${adminToken}` }
            });

            setNewDeliveryCity('');
            setNewDeliveryFee('');
            setNewDeliveryFreeMin('');
            fetchDeliverySettings();
        } catch (error) {
            console.error('Error adding delivery setting:', error);
            alert(error.response?.data?.message || 'Failed to add delivery setting');
        }
    };

    const startEditingOrderDiscount = () => {
        if (orderDiscount) {
            const hasLimit = parseFloat(orderDiscount.min_price || 0) > 0;
            setHasMinPriceLimit(hasLimit);
            setEditOrderDiscount({
                type: orderDiscount.type,
                value: orderDiscount.value,
                start_date: orderDiscount.start_date ? new Date(orderDiscount.start_date).toISOString().slice(0, 10) : '',
                end_date: orderDiscount.end_date ? new Date(orderDiscount.end_date).toISOString().slice(0, 10) : '',
                is_active: !!orderDiscount.is_active,
                min_price: orderDiscount.min_price !== undefined && orderDiscount.min_price !== null ? orderDiscount.min_price.toString() : '0'
            });
        } else {
            setHasMinPriceLimit(false);
            setEditOrderDiscount({
                type: 'percentage',
                value: '',
                start_date: new Date().toISOString().slice(0, 10),
                end_date: '',
                is_active: true,
                min_price: '0'
            });
        }
        setIsEditingOrderDiscount(true);
    };

    const handleSaveOrderDiscount = async () => {
        const adminToken = localStorage.getItem('adminToken');

        // Validate inputs
        if (!editOrderDiscount.type || !editOrderDiscount.value) {
            alert('Please fill in all required fields (Type and Value)');
            return;
        }

        const discountValue = parseFloat(editOrderDiscount.value);
        if (isNaN(discountValue) || discountValue < 0) {
            alert('Discount value must be a valid positive number');
            return;
        }

        try {
            const minPriceValue = editOrderDiscount.min_price ? parseFloat(editOrderDiscount.min_price) : 0;
            const shouldActivate = editOrderDiscount.is_active || (hasMinPriceLimit && minPriceValue > 0);

            const payload = {
                type: editOrderDiscount.type,
                value: discountValue, // Send as number, not string
                start_date: editOrderDiscount.start_date || null,
                end_date: editOrderDiscount.end_date || null,
                is_active: shouldActivate ? 1 : 0,
                min_price: minPriceValue
            };

            // If updating existing record, use PUT with id
            if (orderDiscount && orderDiscount.id) {
                await axios.put(`${API_BASE_URL}/admin/order-discounts/${orderDiscount.id}`, payload, {
                    headers: { Authorization: `Bearer ${adminToken}` }
                });
            } else {
                // If creating new record, use POST
                await axios.post(`${API_BASE_URL}/admin/order-discounts`, payload, {
                    headers: { Authorization: `Bearer ${adminToken}` }
                });
            }

            fetchOrderDiscount();
            setIsEditingOrderDiscount(false);
            alert('Order discount saved successfully!');
        } catch (error) {
            console.error('Error saving order discount:', error);
            alert('Failed to save order discount: ' + (error.response?.data?.message || error.message));
        }
    };

    return (
        <div className="categories-container definitions-container">
            <AdminSidebar />
            <div className="categories-main definitions-main">

                <div className="hierarchy-grid">
                    {/* Section 1: Seasons */}
                    <div className="hierarchy-section">
                        <div className="section-header">
                            <h3>Seasons</h3>
                            <button className="view-all-btn" onClick={() => openListModal('seasons', 'All Seasons')}>View All</button>
                        </div>
                        <div className="quick-add-column">
                            <div className="form-group">
                                <div className="side-by-side-inputs">
                                    <input
                                        type="text"
                                        placeholder="Season Name"
                                        style={{ flex: 2 }}
                                        value={newSeasonName}
                                        onChange={(e) => setNewSeasonName(e.target.value)}
                                        onKeyPress={(e) => e.key === 'Enter' && handleAdd('seasons', newSeasonName)}
                                    />
                                    <input
                                        type="number"
                                        placeholder="Year"
                                        style={{ flex: 1 }}
                                        value={newSeasonYear}
                                        onChange={(e) => setNewSeasonYear(e.target.value)}
                                        onKeyPress={(e) => e.key === 'Enter' && handleAdd('seasons', newSeasonName)}
                                    />
                                </div>
                            </div>
                            <button className="submit-add-btn" onClick={() => handleAdd('seasons', newSeasonName)}>
                                Add Season
                            </button>
                        </div>
                    </div>

                    {/* Section 2: Attribute Families */}
                    <div className="hierarchy-section">
                        <div className="section-header">
                            <h3>Attribute Families</h3>
                            <button className="view-all-btn" onClick={() => openListModal('attribute-families', 'All Families')}>View All</button>
                        </div>
                        <div className="quick-add-column">
                            <div className="form-group">
                                <select
                                    className="parent-select"
                                    value={selectedAttrForFamily}
                                    onChange={(e) => setSelectedAttrForFamily(e.target.value)}
                                >
                                    <option value="">-- Select Attribute --</option>
                                    {attributes.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                                </select>
                            </div>
                            <div className="form-group">
                                <input
                                    type="text"
                                    placeholder="Family Name"
                                    value={newAttributeFamilyName}
                                    onChange={(e) => setNewAttributeFamilyName(e.target.value)}
                                    onKeyPress={(e) => e.key === 'Enter' && handleAdd('attribute-families', newAttributeFamilyName, selectedAttrForFamily)}
                                />
                            </div>
                            <button className="submit-add-btn" onClick={() => handleAdd('attribute-families', newAttributeFamilyName, selectedAttrForFamily)}>
                                Add Family
                            </button>
                        </div>
                    </div>

                    {/* Section 3: Attribute Values */}
                    <div className="hierarchy-section">
                        <div className="section-header">
                            <h3>Attribute Values</h3>
                            <button className="view-all-btn" onClick={() => openListModal('attribute-values', 'All Attribute Values')}>View All</button>
                        </div>
                        <div className="quick-add-column">
                            <div className="form-group">
                                <select
                                    className="parent-select"
                                    value={selectedAttribute?.id || ''}
                                    onChange={(e) => {
                                        const attr = attributes.find(a => a.id === parseInt(e.target.value));
                                        setSelectedAttribute(attr);
                                        setSelectedFamilyForValue('');
                                    }}
                                >
                                    <option value="">-- Select Attribute --</option>
                                    {attributes.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                                </select>
                            </div>
                            <div className="form-group">
                                <select
                                    className="parent-select"
                                    disabled={!selectedAttribute}
                                    value={selectedFamilyForValue}
                                    onChange={(e) => setSelectedFamilyForValue(e.target.value)}
                                >
                                    <option value="">-- Select Family (Optional) --</option>
                                    {attributeFamilies
                                        .filter(f => f.attribute_id === selectedAttribute?.id)
                                        .map(f => <option key={f.id} value={f.id}>{f.name}</option>)
                                    }
                                </select>
                            </div>
                            <div className="form-group">
                                <input
                                    type="text"
                                    placeholder="Attribute Value"
                                    value={newAttributeValueName}
                                    onChange={(e) => setNewAttributeValueName(e.target.value)}
                                    onKeyPress={(e) => e.key === 'Enter' && handleAdd('attribute-values', newAttributeValueName, selectedAttribute?.id)}
                                />
                            </div>
                            <button className="submit-add-btn" onClick={() => handleAdd('attribute-values', newAttributeValueName, selectedAttribute?.id)}>
                                Add Value
                            </button>
                        </div>
                    </div>

                    {/* Section 3: Delivery Settings */}
                    <div className="hierarchy-section delivery-settings-section">
                        <div className="section-header">
                            <h3>Delivery Settings</h3>
                            {!isEditingDelivery && (
                                <>
                                    <button className="view-all-btn" onClick={() => openListModal('delivery-settings', 'Delivery Fees by City')}>View All</button>
                                    <button className="view-all-btn" onClick={() => startEditingDelivery()}>Edit Default</button>
                                </>
                            )}
                        </div>
                        <div className="quick-add-column">
                            {isEditingDelivery ? (
                                <>
                                    <div className="side-by-side-inputs">
                                        <div className="form-group" style={{ flex: 1 }}>
                                            <label>City</label>
                                            <input
                                                type="text"
                                                value={editDeliveryCity}
                                                disabled={isEditingDefaultDelivery}
                                                placeholder={isEditingDefaultDelivery ? 'Default' : 'Enter city name'}
                                                onChange={(e) => setEditDeliveryCity(e.target.value)}
                                            />
                                        </div>
                                        <div className="form-group" style={{ flex: 1, marginLeft: '12px' }}>
                                            <label>Delivery Fee ($)</label>
                                            <input
                                                type="number"
                                                step="0.01"
                                                value={editDeliveryFee}
                                                onChange={(e) => setEditDeliveryFee(e.target.value)}
                                            />
                                        </div>
                                    </div>
                                    <div className="form-group">
                                        <label>Free Delivery Min ($)</label>
                                        <input
                                            type="number"
                                            step="0.01"
                                            value={editFreeMin}
                                            onChange={(e) => setEditFreeMin(e.target.value)}
                                        />
                                    </div>
                                    <div className="delivery-edit-actions">
                                        <button className="submit-add-btn" onClick={handleSaveDelivery}>Save</button>
                                        <button className="submit-add-btn cancel-btn" onClick={() => setIsEditingDelivery(false)}>Cancel</button>
                                    </div>
                                </>
                            ) : (
                                <>
                                    <div className="delivery-info-display">
                                        <div className="info-row">
                                            <span className="label">Default City:</span>
                                            <span className="value">{defaultDeliverySetting ? 'Default' : 'None'}</span>
                                        </div>
                                        <div className="info-row">
                                            <span className="label">Default Fee:</span>
                                            <span className="value">${defaultDeliverySetting?.delivery_fee || '0.00'}</span>
                                        </div>
                                        <div className="info-row">
                                            <span className="label">Free Above:</span>
                                            <span className="value">${defaultDeliverySetting?.free_delivery_min || '0.00'}</span>
                                        </div>
                                    </div>
                                    <div className="delivery-add-city">
                                        <h4>Add City Fee</h4>
                                        <div className="side-by-side-inputs">
                                            <div className="form-group" style={{ flex: 1 }}>
                                                <label>City</label>
                                                <input
                                                    type="text"
                                                    placeholder="City"
                                                    value={newDeliveryCity}
                                                    onChange={(e) => setNewDeliveryCity(e.target.value)}
                                                />
                                            </div>
                                            <div className="form-group" style={{ flex: 1, marginLeft: '12px' }}>
                                                <label>Delivery Fee ($)</label>
                                                <input
                                                    type="number"
                                                    step="0.01"
                                                    placeholder="Fee"
                                                    value={newDeliveryFee}
                                                    onChange={(e) => setNewDeliveryFee(e.target.value)}
                                                />
                                            </div>
                                        </div>
                                        <div className="form-group">
                                            <label>Free Delivery Min ($)</label>
                                            <input
                                                type="number"
                                                step="0.01"
                                                placeholder="Free above"
                                                value={newDeliveryFreeMin}
                                                onChange={(e) => setNewDeliveryFreeMin(e.target.value)}
                                            />
                                        </div>
                                        <button className="submit-add-btn" onClick={handleAddDeliverySetting}>Add City Fee</button>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>

                    {/* Section 4: Coupons */}
                    <div className="hierarchy-section coupons-section">
                        <div className="section-header">
                            <h3>Coupons</h3>
                            <button className="view-all-btn" onClick={() => openListModal('coupons', 'All Coupons')}>View All</button>
                        </div>
                        <div className="quick-add-column">
                            <div className="form-group">
                                <div className="side-by-side-inputs">
                                    <input
                                        type="text"
                                        placeholder="Coupon Code"
                                        value={newCoupon.code}
                                        onChange={(e) => setNewCoupon({ ...newCoupon, code: e.target.value })}
                                        style={{ flex: 2 }}
                                    />
                                    <select
                                        value={newCoupon.discount_type}
                                        onChange={(e) => setNewCoupon({ ...newCoupon, discount_type: e.target.value })}
                                        style={{ flex: 0.8, minWidth: '70px' }}
                                    >
                                        <option value="percentage">%</option>
                                        <option value="fixed">$</option>
                                    </select>
                                    <input
                                        type="number"
                                        placeholder="Value"
                                        value={newCoupon.discount_value}
                                        onChange={(e) => setNewCoupon({ ...newCoupon, discount_value: e.target.value })}
                                        style={{ flex: 1 }}
                                    />
                                </div>
                            </div>
                            <div className="side-by-side-group">
                                <div className="form-group">
                                    <label className="input-label-small">Start Date</label>
                                    <input
                                        type="date"
                                        value={newCoupon.start_date}
                                        onChange={(e) => setNewCoupon({ ...newCoupon, start_date: e.target.value })}
                                    />
                                </div>
                                <div className="form-group">
                                    <label className="input-label-small">End Date</label>
                                    <input
                                        type="date"
                                        value={newCoupon.end_date}
                                        onChange={(e) => setNewCoupon({ ...newCoupon, end_date: e.target.value })}
                                    />
                                </div>
                            </div>
                            <div className="form-group" style={{ marginTop: '10px', marginBottom: '10px' }}>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                                    <input
                                        type="checkbox"
                                        checked={newCoupon.for_new_signup}
                                        onChange={(e) => setNewCoupon({ ...newCoupon, for_new_signup: e.target.checked })}
                                    />
                                    <span>Just for new signup</span>
                                </label>
                            </div>
                            <button className="submit-add-btn" onClick={() => handleAdd('coupons', newCoupon.code)}>
                                Add Coupon
                            </button>
                        </div>
                    </div>

                    {/* Section 5: Order Discount */}
                    <div className="hierarchy-section">
                        <div className="section-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <h3>General Order Discount</h3>
                            {isEditingOrderDiscount && (
                                <label className="toggle-switch">
                                    <input
                                        type="checkbox"
                                        checked={editOrderDiscount.is_active}
                                        onChange={() => toggleOrderDiscountActive({ ...orderDiscount, ...editOrderDiscount })}
                                    />
                                    <span className="toggle-slider"></span>
                                </label>
                            )}
                        </div>
                        <div className="quick-add-column delivery-settings-section">
                            {isEditingOrderDiscount ? (
                                <>
                                    <div className="form-group">
                                        <label>Discount Type & Value</label>
                                        <div className="side-by-side-inputs">
                                            <select
                                                value={editOrderDiscount.type}
                                                onChange={(e) => setEditOrderDiscount({ ...editOrderDiscount, type: e.target.value })}
                                                style={{ width: '65px', minWidth: '65px', flex: 'none' }}
                                            >
                                                <option value="percentage">%</option>
                                                <option value="fixed">$</option>
                                            </select>
                                            <input
                                                type="number"
                                                placeholder="e.g. 10"
                                                value={editOrderDiscount.value}
                                                onChange={(e) => setEditOrderDiscount({ ...editOrderDiscount, value: e.target.value })}
                                                style={{ flex: 1 }}
                                            />
                                        </div>
                                    </div>
                                    <div className="form-group" style={{ marginTop: '12px' }}>
                                        <label>Discount Condition</label>
                                        <div className="radio-group">
                                            <label>
                                                <input
                                                    type="radio"
                                                    name="discountCondition"
                                                    checked={!hasMinPriceLimit}
                                                    onChange={() => {
                                                        setHasMinPriceLimit(false);
                                                        setEditOrderDiscount({ ...editOrderDiscount, min_price: '0' });
                                                    }}
                                                />
                                                Normal (All orders)
                                            </label>
                                            <label>
                                                <input
                                                    type="radio"
                                                    name="discountCondition"
                                                    checked={hasMinPriceLimit}
                                                    onChange={() => {
                                                        setHasMinPriceLimit(true);
                                                        setEditOrderDiscount({ ...editOrderDiscount, min_price: editOrderDiscount.min_price && parseFloat(editOrderDiscount.min_price) > 0 ? editOrderDiscount.min_price : '' });
                                                    }}
                                                />
                                                Above specific cost
                                            </label>
                                        </div>
                                    </div>
                                    {hasMinPriceLimit && (
                                        <div className="form-group" style={{ marginTop: '12px' }}>
                                            <label>Min Purchase Subtotal ($)</label>
                                            <input
                                                type="number"
                                                step="0.01"
                                                placeholder="e.g. 100"
                                                value={editOrderDiscount.min_price}
                                                onChange={(e) => setEditOrderDiscount({ ...editOrderDiscount, min_price: e.target.value })}
                                            />
                                        </div>
                                    )}
                                    <div className="side-by-side-group" style={{ marginTop: '12px' }}>
                                        <div className="form-group">
                                            <label className="input-label-small">Start Date</label>
                                            <input
                                                type="date"
                                                value={editOrderDiscount.start_date}
                                                onChange={(e) => setEditOrderDiscount({ ...editOrderDiscount, start_date: e.target.value })}
                                            />
                                        </div>
                                        <div className="form-group">
                                            <label className="input-label-small">End Date</label>
                                            <input
                                                type="date"
                                                value={editOrderDiscount.end_date}
                                                onChange={(e) => setEditOrderDiscount({ ...editOrderDiscount, end_date: e.target.value })}
                                            />
                                        </div>
                                    </div>
                                    <div className="delivery-edit-actions">
                                        <button className="submit-add-btn" onClick={handleSaveOrderDiscount}>Save</button>
                                        <button className="submit-add-btn cancel-btn" onClick={() => setIsEditingOrderDiscount(false)}>Cancel</button>
                                    </div>
                                </>
                            ) : (
                                <div className="delivery-info-display">
                                    {orderDiscount ? (
                                        <>
                                            <div className="info-row">
                                                <span className="label">Current Discount:</span>
                                                <span className="value">
                                                    {orderDiscount.type === 'percentage' ? orderDiscount.value + '%' : '$' + orderDiscount.value}
                                                </span>
                                            </div>
                                            <div className="info-row">
                                                <span className="label">Min Order Subtotal:</span>
                                                <span className="value">
                                                    {parseFloat(orderDiscount.min_price || 0) > 0 ? '$' + parseFloat(orderDiscount.min_price).toFixed(2) : 'No minimum (All orders)'}
                                                </span>
                                            </div>
                                            <div className="info-row">
                                                <span className="label">Status:</span>
                                                <span className={`coupon-status-pill ${orderDiscount.is_active ? 'active' : 'inactive'}`} style={{ margin: 0 }}>
                                                    {orderDiscount.is_active ? 'Active' : 'Inactive'}
                                                </span>
                                            </div>
                                            <div className="info-row">
                                                <span className="label">Valid Until:</span>
                                                <span className="value" style={{ fontSize: '0.9rem' }}>
                                                    {orderDiscount.end_date ? new Date(orderDiscount.end_date).toLocaleDateString() : 'No expiry'}
                                                </span>
                                            </div>
                                            <button className="submit-add-btn" style={{ marginTop: '8px' }} onClick={startEditingOrderDiscount}>
                                                Update Discount
                                            </button>
                                        </>
                                    ) : (
                                        <div className="empty-state" style={{ padding: '20px 0' }}>
                                            <p>No order discount set.</p>
                                            <button className="submit-add-btn" onClick={startEditingOrderDiscount}>
                                                Set Discount
                                            </button>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* List Management Modal */}
            {listModal.show && (
                <div className="modal-overlay">
                    <div className="list-management-dialog">
                        <div className="modal-header">
                            <div className="modal-header-left">
                                <h3>{listModal.title}</h3>
                                <div className="modal-search-inline">
                                    <input
                                        type="text"
                                        placeholder={`Search ${listModal.title}...`}
                                        value={
                                            listModal.type === 'seasons' ? searchSeason :
                                                listModal.type === 'attribute-families' ? searchAttributeFamily :
                                                    listModal.type === 'attribute-values' ? searchAttributeValue :
                                                        listModal.type === 'delivery-settings' ? searchDeliverySettings :
                                                            searchCoupon
                                        }
                                        onChange={(e) => {
                                            const val = e.target.value;
                                            if (listModal.type === 'seasons') setSearchSeason(val);
                                            if (listModal.type === 'attribute-families') setSearchAttributeFamily(val);
                                            if (listModal.type === 'attribute-values') setSearchAttributeValue(val);
                                            if (listModal.type === 'delivery-settings') setSearchDeliverySettings(val);
                                            if (listModal.type === 'coupons') setSearchCoupon(val);
                                        }}
                                    />
                                </div>
                            </div>
                            <button className="close-modal-btn" onClick={closeListModal}>×</button>
                        </div>

                        {listModal.type === 'attribute-families' && (
                            <div className="modal-filters">
                                <button
                                    className={`filter-btn ${modalFilterAttributeId === null ? 'active' : ''}`}
                                    onClick={() => setModalFilterAttributeId(null)}
                                >
                                    All
                                </button>
                                {attributes.map(attr => (
                                    <button
                                        key={attr.id}
                                        className={`filter-btn ${modalFilterAttributeId === attr.id ? 'active' : ''}`}
                                        onClick={() => setModalFilterAttributeId(attr.id)}
                                    >
                                        {attr.name}
                                    </button>
                                ))}
                            </div>
                        )}

                        {listModal.type === 'attribute-values' && (
                            <div className="modal-filters">
                                <button
                                    className={`filter-btn ${modalFilterAttributeId === null ? 'active' : ''}`}
                                    onClick={() => setModalFilterAttributeId(null)}
                                >
                                    All
                                </button>
                                {attributes.map(attr => (
                                    <button
                                        key={attr.id}
                                        className={`filter-btn ${modalFilterAttributeId === attr.id ? 'active' : ''}`}
                                        onClick={() => setModalFilterAttributeId(attr.id)}
                                    >
                                        {attr.name}
                                    </button>
                                ))}
                            </div>
                        )}

                        <div className="modal-items-list">
                            {(listModal.type === 'seasons' ? seasons.filter(s => s.name.toLowerCase().includes(searchSeason.toLowerCase())) :
                                listModal.type === 'attribute-families' ? attributeFamilies
                                    .filter(af => modalFilterAttributeId === null || af.attribute_id === modalFilterAttributeId)
                                    .filter(af => af.name.toLowerCase().includes(searchAttributeFamily.toLowerCase())) :
                                    listModal.type === 'attribute-values' ? attributeValues
                                        .filter(av => modalFilterAttributeId === null || av.attribute_id === modalFilterAttributeId)
                                        .filter(av => av.value.toLowerCase().includes(searchAttributeValue.toLowerCase())) :
                                        listModal.type === 'delivery-settings' ? deliverySettings
                                            .filter(ds => {
                                                const search = searchDeliverySettings.toLowerCase();
                                                const cityText = ds.city ? ds.city.toLowerCase() : 'default';
                                                return cityText.includes(search) || `${ds.delivery_fee || ''}`.includes(search) || `${ds.free_delivery_min || ''}`.includes(search);
                                            }) :
                                            coupons.filter(c => c.code.toLowerCase().includes(searchCoupon.toLowerCase()))
                            ).map(item => (
                                <div key={item.id} className="hierarchy-item modal-item">
                                    <div className="item-info">
                                        {editingItem.id === item.id && !isMobile ? (
                                            <div className="edit-box-definitions">
                                                <div className="edit-inputs-wrapper">
                                                    <input
                                                        className="inline-edit-input"
                                                        autoFocus
                                                        value={editValue}
                                                        onChange={(e) => setEditValue(e.target.value)}
                                                        onKeyPress={(e) => e.key === 'Enter' && handleUpdate(listModal.type, item.id, editValue)}
                                                    />
                                                    {listModal.type === 'seasons' && (
                                                        <input
                                                            type="number"
                                                            className="inline-edit-input"
                                                            style={{ width: '80px', marginLeft: '10px' }}
                                                            value={editYear}
                                                            onChange={(e) => setEditYear(e.target.value)}
                                                            onKeyPress={(e) => e.key === 'Enter' && handleUpdate(listModal.type, item.id, editValue)}
                                                        />
                                                    )}
                                                    {listModal.type === 'coupons' && editCouponData && (
                                                        <div className="inline-date-inputs" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                            <input
                                                                type="date"
                                                                className="inline-date-input"
                                                                value={editCouponData.start_date}
                                                                onChange={(e) => setEditCouponData({ ...editCouponData, start_date: e.target.value })}
                                                            />
                                                            <input
                                                                type="date"
                                                                className="inline-date-input"
                                                                value={editCouponData.end_date}
                                                                onChange={(e) => setEditCouponData({ ...editCouponData, end_date: e.target.value })}
                                                            />
                                                            <label style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '0.8rem', whiteSpace: 'nowrap', cursor: 'pointer' }}>
                                                                <input
                                                                    type="checkbox"
                                                                    checked={editCouponData.for_new_signup}
                                                                    onChange={(e) => setEditCouponData({ ...editCouponData, for_new_signup: e.target.checked })}
                                                                />
                                                                New Signups
                                                            </label>
                                                        </div>
                                                    )}
                                                </div>
                                                <div className="edit-btns">
                                                    <button className="save-btn-small" onClick={() => handleUpdate(listModal.type, item.id, editValue)}>Save</button>
                                                    <button className="cancel-btn-small" onClick={() => setEditingItem({ type: null, id: null })}>Cancel</button>
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="item-content-wrapper">
                                                <div className="item-text">
                                                    <span className="item-name">
                                                        {listModal.type === 'attribute-values' ? item.value :
                                                            listModal.type === 'delivery-settings' ? (item.city ? item.city : 'Default') :
                                                                listModal.type === 'coupons' ? (
                                                                    <div className="coupon-list-item">
                                                                        <div className="coupon-main-info">
                                                                            <span className="coupon-code-text">{item.code}</span>
                                                                            {item.show_on_home === 1 && <span className="season-selected-pill" style={{ marginLeft: '8px', background: '#f59e0b' }}>Featured</span>}
                                                                        </div>
                                                                        <div className="coupon-sub-info">
                                                                            <span>{item.discount_type === 'percentage' ? item.discount_value + '%' : '$' + item.discount_value}</span>
                                                                            {item.end_date && <span> • Ends: {new Date(item.end_date).toLocaleDateString()}</span>}
                                                                            {item.for_new_signup ? <span style={{ marginLeft: '6px', color: '#10b981', fontWeight: '600' }}>• New Signups</span> : null}
                                                                        </div>
                                                                    </div>
                                                                ) :
                                                                    item.name}
                                                    </span>
                                                    {listModal.type === 'seasons' && (
                                                        <>
                                                            <span className="item-parent" style={{ marginLeft: '10px' }}>({item.year})</span>
                                                            {item.selected === 1 && <span className="season-selected-pill">Active</span>}
                                                        </>
                                                    )}
                                                    {listModal.type === 'delivery-settings' && (
                                                        <span className="item-parent" style={{ marginLeft: '5px', color: '#6b7280' }}>
                                                            ${parseFloat(item.delivery_fee || 0).toFixed(2)}{item.city ? '' : ' • Default'}{item.city ? '' : ` • Free above $${parseFloat(item.free_delivery_min || 0).toFixed(2)}`}
                                                        </span>
                                                    )}
                                                    {item.attributeName && <span className="item-parent">In {item.attributeName}</span>}
                                                    {item.familyName && <span className="item-parent" style={{ marginLeft: '5px', color: '#6b7280' }}>• {item.familyName}</span>}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                    <div className="item-actions">
                                        {listModal.type === 'coupons' && (
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                                                <button
                                                    className={`use-season-btn ${item.show_on_home ? 'active' : ''}`}
                                                    style={item.show_on_home ? { background: '#f59e0b', borderColor: '#f59e0b' } : {}}
                                                    onClick={() => handleToggleCouponFeatured(item)}
                                                >
                                                    {item.show_on_home ? 'Featured' : 'Show on Home'}
                                                </button>
                                                <label className="toggle-switch">
                                                    <input
                                                        type="checkbox"
                                                        checked={!!item.is_active}
                                                        onChange={() => toggleCouponActive(item)}
                                                    />
                                                    <span className="toggle-slider"></span>
                                                </label>
                                            </div>
                                        )}
                                        {listModal.type === 'seasons' && (
                                            <button
                                                className={`use-season-btn ${item.selected ? 'active' : ''}`}
                                                onClick={() => handleToggleSeasonSelected(item)}
                                            >
                                                {item.selected ? 'Selected' : 'Use Season'}
                                            </button>
                                        )}
                                        <button onClick={() => startEditing(listModal.type, item)}>Edit</button>
                                        <button className="del" onClick={() => confirmDelete(listModal.type, item.id)}>Delete</button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {/* Mobile Edit Modal */}
            {editingItem.id && isMobile && (
                <div className="modal-overlay">
                    <div className="confirm-dialog edit-dialog-mobile">
                        <h3>Edit Item</h3>
                        <div className="edit-box-mobile">
                            <div className="form-group">
                                <label>{editingItem.type === 'attribute-values' ? 'Value' : editingItem.type === 'coupons' ? 'Coupon Code' : 'Name'}</label>
                                <input
                                    className="form-group input mobile-edit-input"
                                    style={{ width: '100%', padding: '10px 14px', border: '1px solid #e5e7eb', borderRadius: '8px', marginTop: '5px', marginBottom: '10px' }}
                                    autoFocus
                                    placeholder="Name"
                                    value={editValue}
                                    onChange={(e) => setEditValue(e.target.value)}
                                />
                            </div>

                            {editingItem.type === 'seasons' && (
                                <div className="form-group">
                                    <label>Year</label>
                                    <input
                                        type="number"
                                        className="form-group input mobile-edit-input"
                                        style={{ width: '100%', padding: '10px 14px', border: '1px solid #e5e7eb', borderRadius: '8px', marginTop: '5px', marginBottom: '10px' }}
                                        value={editYear}
                                        onChange={(e) => setEditYear(e.target.value)}
                                    />
                                </div>
                            )}

                            {editingItem.type === 'coupons' && editCouponData && (
                                <div className="coupon-extra-edit" style={{ border: 'none', paddingTop: 0, marginTop: 0 }}>
                                    <div className="side-by-side-group">
                                        <div className="form-group">
                                            <label>Starts At</label>
                                            <input
                                                type="date"
                                                value={editCouponData.start_date}
                                                onChange={(e) => setEditCouponData({ ...editCouponData, start_date: e.target.value })}
                                            />
                                        </div>
                                        <div className="form-group">
                                            <label>Ends At</label>
                                            <input
                                                type="date"
                                                value={editCouponData.end_date}
                                                onChange={(e) => setEditCouponData({ ...editCouponData, end_date: e.target.value })}
                                            />
                                        </div>
                                    </div>
                                    <div className="form-group" style={{ marginTop: '10px' }}>
                                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                                            <input
                                                type="checkbox"
                                                checked={editCouponData.for_new_signup}
                                                onChange={(e) => setEditCouponData({ ...editCouponData, for_new_signup: e.target.checked })}
                                            />
                                            <span>Just for new signup</span>
                                        </label>
                                    </div>
                                </div>
                            )}
                        </div>
                        <div className="dialog-btns">
                            <button className="dialog-cancel-btn" onClick={() => setEditingItem({ type: null, id: null })}>Cancel</button>
                            <button className="submit-add-btn" style={{ width: 'auto', padding: '10px 24px', margin: 0 }} onClick={() => handleUpdate(editingItem.type, editingItem.id, editValue)}>Save Changes</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Toggle Confirmation Dialog */}
            {toggleConfirm.show && (
                <div className="modal-overlay delete-overlay">
                    <div className="confirm-dialog">
                        <h3>Confirm Status Change</h3>
                        <p>
                            Are you sure you want to {toggleConfirm.item?.is_active ? 'deactivate' : 'activate'} {toggleConfirm.type === 'coupons' ? 'coupon' : 'order discount'}
                            {toggleConfirm.type === 'coupons' ? <strong> {toggleConfirm.item?.code}</strong> : ''}?
                        </p>
                        <div className="dialog-btns">
                            <button className="dialog-cancel-btn" onClick={() => setToggleConfirm({ show: false, item: null, type: null })}>Cancel</button>
                            <button className="dialog-delete-btn" style={{ background: toggleConfirm.item?.is_active ? '#ef4444' : '#10b981' }} onClick={handleConfirmToggle}>
                                Yes, {toggleConfirm.item?.is_active ? 'Deactivate' : 'Activate'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Custom Delete Confirmation Dialog */}
            {deleteConfirm.show && (
                <div className="modal-overlay delete-overlay">
                    <div className="confirm-dialog">
                        <h3>Confirm Delete</h3>
                        <p>Are you sure you want to delete this item? This action cannot be undone.</p>
                        <div className="dialog-btns">
                            <button className="dialog-cancel-btn" onClick={() => setDeleteConfirm({ show: false, type: null, id: null })}>No, Cancel</button>
                            <button className="dialog-delete-btn" onClick={handleDelete}>Yes, Delete</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Definitions;
